import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaBox, FaLeaf, FaSearch, FaSync } from "react-icons/fa";
import AdminLayout from "../../components/AdminLayout";
import api from "../../utils/api";
import { usePopup } from "../../context/PopupContext";
import { getImageUrl } from "../../utils/imageUtils";
import { useTranslation } from "react-i18next";
import { toLocalizedNumber, toLocalizedPrice } from "../../utils/localeUtils";
import Pagination from "../../components/Pagination";
import "./AdminProductManagement.css";

const PER_PAGE = 10;

const AdminProductManagement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast, showConfirm } = usePopup();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [page, setPage] = useState(1);

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        const userObj = JSON.parse(userData);
        setUser(userObj);
        if (userObj.role !== "admin") {
          navigate("/");
        } else {
          fetchProducts();
        }
      } catch (error) {
        console.error("Error parsing user data:", error);
        setLoading(false);
      }
    } else {
      navigate("/login");
    }
  }, [navigate]);

  // Refresh products when admin returns to the tab (so stock is up to date)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible" && user?.role === "admin") {
        fetchProducts();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, [user?.role]);

  // Refresh product list every 60s so stock stays current (without showing loading)
  useEffect(() => {
    if (user?.role !== "admin") return;
    const interval = setInterval(() => fetchProducts(false), 60000);
    return () => clearInterval(interval);
  }, [user?.role]);

  const fetchProducts = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const response = await api.get("/admin/products");
      setProducts(response.data || []);
    } catch (error) {
      console.error("Error fetching products:", error);
      setProducts([]);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const handleDelete = async (productId) => {
    showConfirm({
      title: t("adminProductManagement.deleteTitle"),
      message: t("adminProductManagement.deleteMessage"),
      confirmLabel: t("adminProductManagement.delete"),
      cancelLabel: t("common.cancel"),
      onConfirm: async () => {
        try {
          await api.delete(`/products/${productId}`);
          fetchProducts();
          showToast(t("adminProductManagement.deleteSuccess"), "success");
        } catch (error) {
          console.error("Error deleting product:", error);
          showToast(t("adminProductManagement.deleteFailed"), "error");
        }
      },
    });
  };

  const filteredProducts = products.filter((product) => {
    const matchesCategory =
      filterCategory === "all" ||
      product.category?.toLowerCase() === filterCategory.toLowerCase();
    const matchesSearch =
      product.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.farmer?.name?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const totalPages = Math.ceil(filteredProducts.length / PER_PAGE);
  const pageProducts = filteredProducts.slice(
    (page - 1) * PER_PAGE,
    page * PER_PAGE,
  );

  useEffect(() => {
    setPage(1);
  }, [searchQuery, filterCategory]);

  const categories = [
    "Vegetables",
    "Fruits",
    "Grains",
    "Dairy",
    "Spices",
    "Other",
  ];

  const stats = {
    total: products.length,
    active: products.filter((p) => p.isAvailable).length,
    organic: products.filter((p) => p.isOrganic).length,
  };

  return (
    <AdminLayout>
      <div className="admin-product-management-content">
        {/* Page Header */}
        <div className="admin-product-management-page-header">
          <div>
            <h1 className="admin-product-management-page-title">
              {t("adminProductManagement.title")}
            </h1>
            <p className="admin-product-management-page-subtitle">
              {t("adminProductManagement.subtitle")}
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="admin-product-management-stats-cards-grid">
          <div className="admin-product-management-stat-card">
            <div className="admin-product-management-stat-icon">
              <FaBox aria-hidden />
            </div>
            <div className="admin-product-management-stat-content">
              <div className="admin-product-management-stat-value">
                {toLocalizedNumber(stats.total)}
              </div>
              <div className="admin-product-management-stat-label">
                {t("adminProductManagement.totalProducts")}
              </div>
            </div>
          </div>
          <div className="admin-product-management-stat-card">
            <div className="admin-product-management-stat-icon">
              <FaLeaf aria-hidden />
            </div>
            <div className="admin-product-management-stat-content">
              <div className="admin-product-management-stat-value">
                {toLocalizedNumber(stats.organic)}
              </div>
              <div className="admin-product-management-stat-label">
                {t("adminProductManagement.organicProducts")}
              </div>
            </div>
          </div>
          <div className="admin-product-management-stat-card">
            <div className="admin-product-management-stat-icon">
              <FaBox aria-hidden />
            </div>
            <div className="admin-product-management-stat-content">
              <div className="admin-product-management-stat-value">
                {toLocalizedNumber(stats.active)}
              </div>
              <div className="admin-product-management-stat-label">
                Active Listings
              </div>
            </div>
          </div>
        </div>

        {/* Filters Section */}
        <div className="admin-product-management-filters-card">
          <div className="admin-product-management-search-box">
            <input
              type="text"
              placeholder={t("adminProductManagement.searchPlaceholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="admin-product-management-search-input"
            />
            <FaSearch
              className="admin-product-management-search-icon"
              aria-hidden
            />
          </div>
          <div className="admin-product-management-filter-group">
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="admin-product-management-filter-select"
            >
              <option value="all">
                {t("adminProductManagement.allCategories")}
              </option>
              {categories.map((cat) => (
                <option key={cat} value={cat.toLowerCase()}>
                  {cat}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => fetchProducts()}
              disabled={loading}
              className="admin-product-management-refresh-btn"
              title="Refresh to see latest stock and products"
            >
              <>
                <FaSync
                  className={
                    loading ? "admin-product-management-refresh-spinner" : ""
                  }
                  aria-hidden
                />{" "}
                {loading
                  ? t("adminProductManagement.refreshing")
                  : t("adminProductManagement.refresh")}
              </>
            </button>
          </div>
        </div>

        {/* Products Table */}
        <div className="admin-product-management-table-card">
          {loading ? (
            <div className="admin-product-management-loading-state">
              <div className="admin-product-management-spinner"></div>
              <p>{t("adminProductManagement.loading")}</p>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="admin-product-management-empty-state">
              <div className="admin-product-management-empty-icon">
                <FaBox aria-hidden />
              </div>
              <h3>{t("adminProductManagement.noProducts")}</h3>
              <p>{t("adminProductManagement.tryAdjusting")}</p>
            </div>
          ) : (
            <div className="admin-product-management-table-container">
              <table className="admin-product-management-data-table">
                <thead>
                  <tr>
                    <th>{t("adminProductManagement.image")}</th>
                    <th>{t("adminProductManagement.productName")}</th>
                    <th>{t("adminProductManagement.farmer")}</th>
                    <th>{t("adminProductManagement.price")}</th>
                    <th>{t("adminProductManagement.category")}</th>
                    <th>{t("adminProductManagement.stock")}</th>
                    <th>{t("adminProductManagement.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {pageProducts.map((product) => (
                    <tr key={product._id}>
                      <td>
                        <img
                          src={
                            product.images?.length
                              ? getImageUrl(product.images[0])
                              : "/images/default-product.png"
                          }
                          alt={product.name}
                          className="admin-product-management-product-thumb"
                        />
                      </td>
                      <td>
                        <div className="admin-product-management-admin-product-management-product-name-cell">
                          <div className="admin-product-management-product-name">
                            {product.name}
                          </div>
                          {product.isOrganic && (
                            <span className="admin-product-management-organic-badge">
                              Organic
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <Link
                          to={`/users/${product.farmer?.username || product.farmer?._id}`}
                          className="admin-product-management-farmer-link"
                        >
                          {product.farmer?.name || "N/A"}
                        </Link>
                      </td>
                      <td>
                        Rs. {toLocalizedPrice(product.price)} / {product.unit}
                      </td>
                      <td>{product.category || "N/A"}</td>
                      <td>
                        <span
                          className={`admin-product-management-stock-badge ${(product.quantity ?? product.stock) > 10 ? "in-stock" : (product.quantity ?? product.stock) > 0 ? "low-stock" : "out-of-stock"}`}
                        >
                          {toLocalizedNumber(
                            product.quantity ?? product.stock ?? 0,
                          )}{" "}
                          {product.unit}
                        </span>
                      </td>
                      <td className="admin-product-management-actions-cell">
                        <div className="admin-product-management-action-buttons">
                          <Link
                            to={`/admin/products/edit/${product._id}`}
                            className="admin-product-management-action-btn view"
                          >
                            {t("adminProductManagement.edit")}
                          </Link>
                          <button
                            onClick={() => handleDelete(product._id)}
                            className="admin-product-management-action-btn delete"
                          >
                            {t("adminProductManagement.delete")}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pagination
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminProductManagement;
